import React, { useCallback, useState, useEffect } from "react";
import api from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import RefreshButton from './RefreshButton';

const getStatusDot = (status) => {
  switch (status) {
    case 'in_review':
    case 'pending':
    case 'unresolved':
      return 'bg-red-500';
    case 'resolved':
    case 'closed':
      return 'bg-green-500';
    default:
      return 'bg-gray-400';
  }
};

const formatTimeAgo = (dateString) => {
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true })
      .replace('about ', '')
      .replace('less than a minute ago', 'just now')
      .replace('minute', 'min');
  } catch (e) {
    return dateString;
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'in_review':
      return 'In Review';
    case 'resolved':
      return 'Resolved';
    case 'pending':
      return 'Pending';
    case 'closed':
      return 'Closed';
    default:
      return 'Unresolved';
  }
};

const Complaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/v1/admin/complaints');
      setComplaints(response.data?.data || response.data || []);
      setError(null);
      setUpdatedAt(new Date());
    } catch (err) {
      console.error('Error fetching complaints:', err?.response?.status ?? '', err?.message ?? '');
      // Keep the complaints already on screen; the badge says the refresh failed.
      setError('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  // Count unresolved complaints
  const unresolvedCount = complaints.filter(
    complaint => complaint.status !== 'resolved' && complaint.status !== 'closed'
  ).length;

  if (loading && !updatedAt && complaints.length === 0) {
    return (
      <div className="bg-white dark:bg-black p-4 rounded-xl shadow-sm border border-gray-100 h-full flex flex-col">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Complaints Registered
          </h3>
          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">
            Loading...
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-black p-4 rounded-xl shadow-sm border border-gray-100 h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-gray-800">
          Complaints Registered
        </h3>
        <div className="flex items-center gap-2">
          {error ? (
            <span className="bg-yellow-100 text-yellow-600 text-xs font-medium px-2 py-0.5 rounded-full">
              {error}
            </span>
          ) : (
            <span className={`${unresolvedCount > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'} text-xs font-medium px-2 py-0.5 rounded-full`}>
              {unresolvedCount} {unresolvedCount === 1 ? 'unresolved' : 'unresolved'}
            </span>
          )}
          <RefreshButton onRefresh={fetchComplaints} loading={loading} updatedAt={updatedAt} />
        </div>
      </div>

      {/* Complaints List */}
      <div className="flex-1 overflow-y-auto max-h-[200px] -mr-2 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
        <div className="space-y-3 pr-1">
          {complaints.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              No complaints found
            </div>
          ) : (
            complaints.slice(0, 5).map((item) => (
              <div
                key={item._id}
                className="flex items-start justify-between text-sm"
              >
                <div className="flex items-start space-x-3 min-w-0">
                  {/* Status dot */}
                  <span
                    className={`flex-shrink-0 w-2 h-2 mt-2 rounded-full ${getStatusDot(
                      item.status
                    )}`}
                    title={getStatusText(item.status)}
                  ></span>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-200">
                      {`#${(item._id || '').slice(-4)}`} • {getStatusText(item.status)}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed break-words">
                      {item.subject || 'No subject'}
                    </p>
                    {item.message && (
                      <p className="text-gray-500 dark:text-gray-500 text-xs mt-1 line-clamp-2">
                        {item.message}
                      </p>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap pl-2">
                  {formatTimeAgo(item.createdAt)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <button className="mt-3 text-xs text-orange-500 hover:text-orange-600 font-medium text-center w-full pt-2 border-t border-gray-100">
        View Details
      </button>
    </div>
  );
};

export default Complaints;
